import { useState } from "react";
import Image from "next/image";

import { BriefcaseIcon, DoorIcon, FilesIcon, UserCheckIcon } from "@/components/icons";
import SectionTag from "@/components/seo/SectionTag";
import { BaseVideoModal } from "@/components/seo/VideoModalWithThumbnail";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import CalendarBookingLink from "./CalendarBookingLink";

const FeatureSection = ({
  icon,
  title,
  description,
  onMouseEnter,
  onMouseLeave,
  hovered,
}: {
  icon: React.ReactNode;
  title: string;
  description: string;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
  hovered: boolean;
}) => {
  return (
    <div className="cursor-default" onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave}>
      <div className="flex items-center xl:justify-center justify-start w-full">
        <div className="h-12 w-12 bg-ash-100 rounded-[10px] flex items-center justify-center mb-4">{icon}</div>
      </div>
      <h3 className="font-semibold mb-2">{title}</h3>
      <p className={cn("lg:invisible text-ash-600 text-sm", hovered && "lg:visible")}>{description}</p>
    </div>
  );
};

const WebsitePreview = ({
  city,
  industry,
  industryServices,
}: {
  city?: string;
  industry: string;
  industryServices?: { label?: string; description?: string }[];
}) => {
  const [videoOpen, setVideoOpen] = useState(false);
  const [hoveredItem, setHoveredItem] = useState<string | null>(null);

  const isIndustrySpecificPage = !city && Array.isArray(industryServices);

  return (
    <section className="lg:pt-20 pt-10 lg:pb-28 pb-20 px-4">
      <div className="text-center mb-12 lg:mb-16">
        <SectionTag text="Where the magic happens" className="mx-auto" />
        <h2 className="text-3xl lg:text-4xl font-semibold text-ash-800 mt-4 text-center">How We Optimize Your Site</h2>
        <Button className="mt-8" variant="outline" onClick={() => setVideoOpen(true)}>
          See how our platform works
        </Button>
        <BaseVideoModal
          open={videoOpen}
          onClose={() => setVideoOpen(false)}
          url="https://www.youtube.com/watch?v=9dKkuuNfNsg"
        />
      </div>

      <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-10">
        <div className="space-y-10 col-span-1 lg:col-span-2 order-2 lg:order-1 px-14 lg:px-1 xl:px-0">
          <FeatureSection
            icon={<DoorIcon />}
            title="Quick Contact Buttons"
            description="Let visitors reach you instantly with clear call, text, and email buttons on every page."
            onMouseEnter={() => setHoveredItem("quick-contact")}
            onMouseLeave={() => setHoveredItem(null)}
            hovered={hoveredItem === "quick-contact"}
          />

          <FeatureSection
            icon={<FilesIcon />}
            title="Instant Value Display"
            description="Visitors see your services and benefits the moment they land on your page."
            onMouseEnter={() => setHoveredItem("instant-value")}
            onMouseLeave={() => setHoveredItem(null)}
            hovered={hoveredItem === "instant-value"}
          />
        </div>

        <div className="relative col-span-1 lg:col-span-8 order-1 lg:order-2">
          {/* connecting lines from text info to preview image *janky* and not responsive so hide when not desktop */}
          <div className="hidden xl:block">
            {/* top left lines */}
            <div
              className={cn(
                "absolute z-1 -left-[22px] top-[60px] w-[142px] h-[270px] bg-transparent border-b-2 border-l-2",
                hoveredItem !== "quick-contact" && "hidden"
              )}
              style={{
                borderImage: "linear-gradient(to top left, rgb(128 128 128) 0%, rgba(128, 128, 128, 0) 100%) 1",
              }}
            >
              <div className="absolute -right-[4px] -bottom-[6px] rounded-full w-2.5 h-2.5 border border-ash-400/90 bg-ash-100" />
            </div>
            <div className={cn(hoveredItem !== "instant-value" && "hidden")}>
              {/* bottom left lines */}
              <div
                className={cn(
                  "absolute -left-[15px] top-[130px] w-[85px] h-[325px] bg-transparent border-r-2 border-b-2"
                )}
                style={{
                  borderImage: "linear-gradient(to bottom left, rgb(128 128 128) 0%, rgba(128, 128, 128, 0) 100%) 1",
                }}
              />
              <div className={cn("absolute z-1 left-[70px] top-[130px] w-[50px] h-[2px] bg-[rgb(128,128,128)]")}>
                <div className="absolute -right-[4px] -top-[4.5px] rounded-full w-2.5 h-2.5 border border-ash-400/90 bg-ash-100" />
              </div>
            </div>

            {/* top right lines */}
            <div
              className={cn(
                "absolute z-1 -right-[22px] top-[32px] w-[125px] h-[85px] bg-transparent border-b-2 border-r-2",
                hoveredItem !== "menu" && "hidden"
              )}
              style={{
                borderImage: "linear-gradient(to top right, rgb(128 128 128) 0%, rgba(128, 128, 128, 0) 100%) 1",
              }}
            >
              <div className="absolute -left-[4px] -bottom-[6px] rounded-full w-2.5 h-2.5 border border-ash-400/90 bg-ash-100" />
            </div>

            {/* bottom right lines */}
            <div
              className={cn(
                "absolute z-1 -right-[22px] top-[400px] w-[159px] h-[118px] bg-transparent border-b-2 border-r-2",
                hoveredItem !== "services" && "hidden"
              )}
              style={{
                borderImage: "linear-gradient(to top right, rgb(128 128 128) 0%, rgba(128, 128, 128, 0) 100%) 1",
              }}
            >
              <div className="absolute -left-[4px] -bottom-[6px] rounded-full w-2.5 h-2.5 border border-ash-400/90 bg-ash-100" />
            </div>
          </div>

          <Image
            src={isIndustrySpecificPage ? "/industry-website-preview.png" : "/website-preview.png"}
            alt="Website preview showing construction company homepage"
            width={1000}
            quality={100}
            height={800}
            priority={false}
            className="w-full h-auto"
          />
        </div>

        <div className="space-y-10 lg:space-y-20 col-span-1 lg:col-span-2 order-3 lg:order-3 px-14 lg:px-1 xl:px-0 relative">
          <FeatureSection
            icon={<UserCheckIcon />}
            title="Always Visible Menu"
            description="Keep important options in view as visitors browse your site."
            onMouseEnter={() => setHoveredItem("menu")}
            onMouseLeave={() => setHoveredItem(null)}
            hovered={hoveredItem === "menu"}
          />

          <FeatureSection
            icon={<BriefcaseIcon />}
            title="Simple Service Display"
            description="Help visitors understand your services quickly with clear icons and descriptions."
            onMouseEnter={() => setHoveredItem("services")}
            onMouseLeave={() => setHoveredItem(null)}
            hovered={hoveredItem === "services"}
          />
        </div>
      </div>

      {isIndustrySpecificPage && (
        <ServicesPreview industry={industry} industryServices={industryServices} className="mt-16" />
      )}

      <div className="text-center mt-16">
        <h3 className="text-2xl font-semibold max-w-lg mx-auto">
          Ready to stand out in the&nbsp;
          {city ? `${city} ${industry}` : `${industry}`}
          &nbsp;industry? Get your website today.
        </h3>
        <CalendarBookingLink className="mx-auto py-3" />
      </div>
    </section>
  );
};

function ServicesPreview({
  className,
  industry,
  industryServices,
}: {
  className?: string;
  industry: string;
  industryServices: { label?: string; description?: string }[];
}) {
  if (!industryServices || industryServices.length === 0) {
    return null;
  }

  return (
    <section className={cn("w-full rounded-[20px] bg-ash-100 py-12 px-4 lg:px-20", className)}>
      <h4 className="text-2xl font-semibold w-full text-center mb-4 md:mb-8">Showcase Your Core {industry} Services</h4>
      <ul className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
        {industryServices.map(({ label: service, description }, index) => (
          <li
            key={index}
            className={cn("p-8 rounded-[10px] w-full flex flex-col", index % 2 === 0 ? "bg-[#A2CD66]" : "bg-[#CAE0AB]")}
          >
            <div className="flex items-center gap-5">
              <div className="min-w-12 h-12 bg-white text-xl rounded-[15px] flex items-center justify-center font-bold border border-ash-300">
                {index + 1}
              </div>
              <h5 className="text-lg font-semibold">{service}</h5>
            </div>
            {description && <p className="text-ash-800 mt-5 mb-4">{description}</p>}
          </li>
        ))}
      </ul>
    </section>
  );
}

export default WebsitePreview;
